import { AfterViewInit, ChangeDetectorRef, Component, ContentChild, ElementRef, Input, NgZone, OnDestroy, TemplateRef, ViewChild } from "@angular/core";
import { NoteService } from "ng/src/app/notes/shared/note.service";
import { ResizeObserverHelper } from "ng/src/app/core/resize-observer-helper";
import { DisplayNote } from "ng/src/app/notes/note-display/note-display.model";
import { CookieService } from "ngx-cookie-service";
import { Utils } from "../../core/utils";
import { fromEvent, Subject, Subscription } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { NoteControlService } from "ng/src/app/notes/note-controller/note-control.service";
import { Note } from "ng/src/app/notes/shared/note.model";
import { NoteControlType } from "ng/src/app/notes/note-controller/note-controller-options.model";

@Component({
	selector: `lb-note[note]`,
	templateUrl: './note-display.component.html',
	styleUrls: ['./note-display.component.scss']
})
export class NoteDisplayComponent implements AfterViewInit, OnDestroy {
	@ViewChild('mainContainer') mainContainer?: ElementRef;
	@ContentChild(TemplateRef) headerTemplate: TemplateRef<ElementRef> | null = null;
	
	@Input() applyStyles: 'all' | 'tagsOnly' | 'none' = 'all';
	@Input() responsiveSize = 420;
	@Input() showExpander = false;
	@Input() styleCard = true;
	@Input() parentTable?: Element;
	@Input() parentTableRow?: HTMLTableRowElement;
	
	@Input() noteEdit: boolean | NoteControlType = true;
	
	@Input()
	get note() {
		return this._note;
	}
	
	set note(val) {
		this._note = val;
		
		setTimeout(() => {
			this.configureState();
			this.changeRef.detectChanges();
		});
	}
	
	@Input()
	get disableLinks() {
		return this._disableLinks;
	}
	set disableLinks(val) {
		this._disableLinks = val;
		this.configureLinkState();
		this.changeRef.detectChanges();
	}
	
	@Input()
	get viewStyle() {
		return this._viewStyle;
	}
	set viewStyle(val) {
		this._viewStyle = val;
		this.configureState();
	}
	
	get noteHtml() {
		return (this.note.showAbridgedNote && typeof this.note.safeHtmlNoteAbridged === 'string') ? this.note.safeHtmlNoteAbridged : this.note.safeHtmlNote;
	}
	
	get noteStyle() {
		let style = '';

		if (this.applyStyles != 'none')
			style = this.noteService.getNoteStyleClasses(this.note, this.applyStyles !== 'tagsOnly').join(' ');

		return style;
	}
	
	get cardStyle() {
		return this.styleCard ? this.noteStyle : '';
	}
	
	viewType: 'STANDARD' | 'CARD' = 'STANDARD';

	private _note!: DisplayNote;
	private _disableLinks = false;
	private _viewStyle: 'STANDARD' | 'CARD' | 'RESPONSIVE' = 'STANDARD';
	private _observerSubscription?: Subscription;
	private _clickSubscription?: Subscription;
	
	private _isInit = false;
	
	private _anchorReplacements = new Map<HTMLAnchorElement, HTMLElement>();
	
	private readonly destroy$ = new Subject();
	
	constructor(
		private noteService: NoteService,
		private noteControl: NoteControlService,
		private changeRef: ChangeDetectorRef,
		private cookieService: CookieService
	) {}

	ngOnInit() {
		this.showExpander && DisplayNote.setAbridgedNote(this.note);
	}
	
	ngAfterViewInit(): void {
		this._isInit = true;
		this.configureState();
		
		this.noteService.register(this.note.id).pipe(
			takeUntil(this.destroy$)
		)
			.subscribe(note => this.refresh(note));
	}
	
	ngOnDestroy(): void {
		this.destroy$.next();
	}

	onNoteExpanderClicked() {
		DisplayNote.toggleShowing(this.note);

		this.note.showAbridgedNote && this.scrollToRowTop();
	}
	
	private refresh(note: Note) {
		const oldNote = this.note;
		const newNote = DisplayNote.setAbridgedNote(note);
		
		this.note = DisplayNote.updateShowing(newNote, oldNote);
		
		
		this.changeRef.detectChanges();
	}
	
	//Replaces all anchor tags with span tags to disable links or vise versa depending on _disableLinks property
	private configureLinkState() {
		let el = this.mainContainer?.nativeElement as HTMLElement | undefined;
		if (!el) {
			return;
		}
		
		const shouldDisableLinks = this._disableLinks || this.cookieService.get('disable-note-links') == 'true';
		
		if (shouldDisableLinks) {
			let anchors = el.querySelectorAll('a');
			
			for (let i = 0; i < anchors.length; i++) {
				let anchor = anchors[i];
				let replacement = this._anchorReplacements.get(anchor);
				if (replacement == null) {
					let innerImg = anchor.querySelector('img');
					if (innerImg == null) {
						replacement = document.createElement('span');
						replacement.textContent = anchor.textContent;
					}
					else {
						replacement = document.createElement('span');
						let newInnerImg = document.createElement('img');
						newInnerImg.src = innerImg.src;
						replacement.appendChild(newInnerImg);
					}
					
					this._anchorReplacements.set(anchor, replacement);
				}
				
				anchor.replaceWith(replacement);
			}
		}
		else {
			for (let [anchor, replacement] of this._anchorReplacements) {
				replacement.replaceWith(anchor);
			}
		}
	}
	
	private setupNoteClick() {
		const el = this.mainContainer?.nativeElement as HTMLElement | undefined;
		if (!el) {
			return;
		}
		
		this._clickSubscription?.unsubscribe();
		this._clickSubscription = fromEvent(el, 'click').pipe(
			takeUntil(this.destroy$)
		)
			.subscribe((event ) => {
				const anchorEl = (event as MouseEvent)?.composedPath().find(el => (el as HTMLElement).nodeName === 'A') as HTMLAnchorElement;
				if (anchorEl && anchorEl.href.includes('editOnLoad')) {
					let segment: number | undefined = undefined;
					
					event.preventDefault();
					event.stopPropagation();
					
					const hashUrl = new URL(anchorEl.href).hash.split('?')[1] ?? '';
					
					if (hashUrl) {
						const params = new URLSearchParams(hashUrl);
						const result = parseInt(params.get('segment') ?? '');
						segment = (!isNaN(result) && result > 0) ? result : result;
					}
					const editorType = typeof this.noteEdit === 'boolean' ? undefined : this.noteEdit;
					this.noteControl.start(this.note as Note, editorType, segment);
				}
			})
	}
	
	configureState() {
		if (!this._isInit)
			return;
		
		const el = this.mainContainer?.nativeElement as HTMLElement | undefined;
		if (!el) {
			return;
		}
		
		this.configureLinkState();
		this.setupNoteClick();
		
		this._observerSubscription?.unsubscribe();
		
		if (this.viewStyle != 'RESPONSIVE') {
			this.viewType = this.viewStyle;
			return;
		}

		this._observerSubscription = ResizeObserverHelper.observe(el).pipe(
			takeUntil(this.destroy$)
		)
			.subscribe((result) => {
				if (!result) return;
				this.viewType = (result.contentRect.width >= this.responsiveSize) ? 'STANDARD' : 'CARD';
		});
	}

	private scrollToRowTop() {
		if (!this.parentTable || !this.parentTableRow)
			return;
		
		const table = this.parentTable!,
			offsetHeight = this.parentTableRow!.offsetTop;
		
		if (Utils.isBetween(offsetHeight, table.scrollTop, table.scrollTop + table.getBoundingClientRect().height))
			return;
		
		this.parentTableRow!.scrollIntoView({ inline: 'start' });
	}
}